<script lang="ts" setup>
import { reactive, ref, onMounted, onBeforeUnmount, computed } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import { email, required } from '@vuelidate/validators'
import { Spin } from 'ant-design-vue'
import BsButton from '@/legacy/components/Button.vue'
import BsDialogFull from '@/legacy/components/DialogFull.vue'
import { createCustomEvent, createGAEvent } from '@/assets/ts/customEvent'
import { instanceBuyandship } from '@/assets/ts/apiManagement'

defineProps({
	text: {
		type: Object,
	},
})

const state = reactive({
	email: '',
})

const emailRef = ref<HTMLInputElement | null>(null)
const isShowPanel = ref(true)
const isLoading = ref(false)
const errorMessage = ref('')
const step = ref(1)

const rules = {
	email: {
		required,
		email,
	},
}

const $v = useVuelidate(rules, state)

const isRequiredInvalidate = computed(() => {
	return $v.value.email.required.$invalid
})

const isEmailInvalidate = computed(() => {
	return $v.value.email.email.$invalid
})

function pressedEnter(e: KeyboardEvent) {
	if (e.key === 'Enter') {
		if (isShowPanel.value) {
			forgetPassword()
		} else {
			hidePanel()
		}
	}
}

function hidePanel() {
	createGAEvent('c_confirm_f_reset_password')
	const payload = {
		type: 'hide',
		data: 'forgetPassword',
	}
	window.postMessage(payload, window.location.origin)
}

function forgetPassword() {
	if ($v.value.$invalid) return

	isLoading.value = true
	createGAEvent('c_confirm_f_req_reset_password')
	instanceBuyandship
		.post('users/request-reset-password', {
			email: state.email,
		})
		.then(() => {
			if (step.value === 1) {
				step.value += 1
			}
			errorMessage.value = ''
			createCustomEvent('member_reset_password_requested', null)
		})
		.catch(err => {
			let message = 'System is busy, please try later'
			if (
				err.response &&
				err.response.data &&
				err.response.data.error &&
				err.response.data.error.message
			) {
				message = err.response.data.error.message
			}
			errorMessage.value = message
		})
		.finally(() => (isLoading.value = false))
}

onMounted(() => {
	if (emailRef.value) emailRef.value.focus()
	window.addEventListener('keydown', pressedEnter)
})

onBeforeUnmount(() => {
	window.removeEventListener('keydown', pressedEnter)
})
</script>

<template>
	<bs-dialog-full
		v-model="isShowPanel"
		class="forgetpassword"
		@on-hide="hidePanel"
	>
		<template #content>
			<div class="bs-dialog-full-slider-container">
				<transition name="dialog-slide">
					<div v-show="step === 1" class="forgetpassword-container" key="step1">
						<div class="forgetpassword-title text-h5">{{ text?.title }}</div>
						<div class="forgetpassword-subtitle">{{ text?.subtitle }}</div>
						<div class="bs-field-group">
							<i class="bs-field-icon icon-mail"></i>
							<input
								class="bs-field"
								:class="{
									'bs-field--error': $v.email.$error,
									'bs-field--fill': $v.email.$model,
								}"
								ref="emailRef"
								v-model.trim="$v.email.$model"
								type="text"
								name="email"
								:placeholder="text?.email.placeholder"
							/>
							<span
								class="bs-field__message"
								:class="{ 'bs-field__message--error': isRequiredInvalidate }"
								v-if="isRequiredInvalidate"
							>
								{{ text?.email.required }}
							</span>
							<span
								class="bs-field__message"
								:class="{ 'bs-field__message--error': isEmailInvalidate }"
								v-if="isEmailInvalidate"
							>
								{{ text?.email.invalid }}
							</span>
						</div>

						<div class="forgetpassword-action">
							<Spin :spinning="isLoading">
								<bs-button
									class="full-width"
									:class="{ 'bs-button--disabled': $v.$invalid }"
									type="primary"
									solid
									@click="forgetPassword"
								>
									{{ text?.button.confirm }}
								</bs-button>
							</Spin>
							<div class="forgetpassword-action-message" v-show="errorMessage">
								{{ errorMessage }}
							</div>
						</div>
					</div>
				</transition>
				<transition name="dialog-slide">
					<div
						v-show="step === 2"
						class="forgetpassword-container forgetpassword-container--step2"
						key="step2"
					>
						<div class="forgetpassword-step2-media">
							<div class="forgetpassword-step2-media__round">
								<slot name="media"></slot>
							</div>
						</div>
						<div class="forgetpassword-step2-title text-h5">
							{{ text?.check.title }}
						</div>
						<div class="forgetpassword-step2-desc">
							{{ text?.check.prefix }}
							<br />
							{{ text?.check.suffix }}
						</div>
						<bs-button
							class="forgetpassword-finish-button"
							type="primary"
							outline
							@click="hidePanel"
						>
							{{ text?.button.ok }}
						</bs-button>
					</div>
				</transition>
			</div>
		</template>
	</bs-dialog-full>
</template>
